/**
 * Shared Light Studio / Refined Dark surface utilities.
 *
 * Prefer these (and Button/Card/Badge variants) over page-local brand hex,
 * gradients, or ghost-accent washes. Tokens live in `styles/tokens.css`.
 */

/**
 * Segmented control track (period 7/30/90, theme switcher, similar toggles).
 * Uses `--segmented-track` so contrast stays correct on Apple canvas + dark.
 */
export const segmentedControlTrackClass =
  'inline-flex gap-0.5 rounded-[10px] bg-[color:var(--segmented-track)] p-[3px]';

/** Inactive segmented item — muted label, hover to foreground. */
export const segmentedControlItemClass =
  'rounded-lg text-[color:var(--muted-foreground)] transition-colors hover:text-foreground';

/** Active segmented thumb — elevated card fill + token shadow. */
export const segmentedControlItemActiveClass =
  'bg-[color:var(--segmented-thumb)] font-semibold text-foreground shadow-[var(--segmented-thumb-shadow)]';

/** Brand gradient fill + white label (avatar fallbacks, logo tiles, marks). */
export const brandGradFillClass =
  'bg-[image:var(--brand-grad)] text-white font-bold';

/**
 * Brand mark / empty-state hero tile: gradient fill + soft blue glow.
 * Use for identity marks and empty CTAs — not for every icon.
 */
export const brandMarkTileClass =
  'bg-[image:var(--brand-grad)] text-white shadow-[var(--shadow-brand-mark)]';

/** Soft brand glow under identity avatars (Profiles identity card). */
export const brandAvatarGlowClass = 'shadow-[var(--shadow-brand-mark)]';

/**
 * Tinted icon tile (linked cards, share chrome). Pair icon with
 * `text-[color:var(--ghost-accent-fg)]`.
 */
export const ghostAccentIconTileClass =
  'bg-[color:var(--ghost-accent-bg)] text-[color:var(--ghost-accent-fg)]';

/** Compact ghost-accent icon button (copy control in share URL field). */
export const ghostAccentIconButtonClass =
  'border-0 bg-[color:var(--ghost-accent-icon-bg)] text-[color:var(--ghost-accent-fg)] shadow-none hover:bg-[color:var(--ghost-accent-icon-bg)] hover:opacity-90';

/**
 * Brand-tinted section title icon (Profiles share / physical cards headers).
 * Pair with Lucide stroke icons — no fill tiles.
 */
export const sectionAccentIconClass =
  'size-[19px] shrink-0 text-[color:var(--ghost-accent-fg)]';

/**
 * Nested secondary wells — linked-card rows, share QR frame, copy field shell.
 * Uses `--secondary` / surface-2 (Light Studio `#fafbfc`, Refined Dark wash).
 */
export const nestedSecondarySurfaceClass = 'border border-border bg-secondary';

/**
 * QR code well. Theme tokens `--qr-well-bg` / `--qr-fg` / `--qr-bg`:
 * light = dark modules on light plate; dark = light modules on solid dark plate.
 */
export const qrWellSurfaceClass =
  'border border-border bg-[color:var(--qr-well-bg)]';

/**
 * Live-preview / device-frame panel surface (Profiles desktop column).
 * Apply on `Card` — not a second Card primitive.
 */
export const previewPanelSurfaceClass =
  'border-[color:var(--preview-panel-border)] bg-[image:var(--preview-panel-bg)]';

/**
 * Live / success pill indicator. Pair inside `Badge variant="success"` so
 * `currentColor` tracks `--ok-fg` (DESIGN.md). Size via caller (`size-1.5`, …).
 */
export const successStatusDotClass = 'shrink-0 rounded-full bg-current';

/**
 * Standalone OK / live solid dot (scan pulse, behaviour chip) — uses `--ok`
 * (theme-aware; bright enough on Refined Dark).
 */
export const statusOkDotClass = 'shrink-0 rounded-full bg-[color:var(--ok)]';

/**
 * Status Board action rail (“Heute zu tun”).
 * Light: primary-tinted elevated surface (hierarchy without a dark ink block).
 * Dark: stronger ink card for night hierarchy.
 */
export const statusBoardInkSurfaceClass =
  'border border-[color:var(--ghost-accent-border)] bg-[color:color-mix(in_oklab,var(--brand-500)_8%,var(--card))] text-foreground shadow-[var(--shadow-card)] dark:border-white/10 dark:bg-[color:#15161a] dark:text-[#f4f3ef]';

export const statusBoardInkRowClass =
  'bg-[color:color-mix(in_oklab,var(--brand-500)_3%,var(--card))] text-foreground hover:bg-[color:color-mix(in_oklab,var(--brand-500)_10%,var(--card))] dark:bg-[color:#1f2025] dark:text-[#f4f3ef] dark:hover:bg-[color:#26272d]';

/**
 * Status Board health surfaces — Signal Red / ok / attention washes.
 * Prefer these over page-local emerald/amber/destructive hex stacks.
 */

/** Critical / sync-failed interrupt (hero + dense rows). */
export const healthCriticalSurfaceClass =
  'border-destructive/30 bg-gradient-to-br from-destructive/[0.12] via-card to-card';

export const healthCriticalGlowClass = 'bg-destructive/20';

export const healthCriticalIconTileClass =
  'bg-destructive text-destructive-foreground';

export const healthCriticalRowClass =
  'bg-destructive/[0.06] hover:bg-destructive/[0.1]';

/** All-clear / healthy — uses `--ok-*` tokens. */
export const healthOkSurfaceClass =
  'border-[color:color-mix(in_oklab,var(--ok)_28%,var(--border))] bg-gradient-to-br from-[color:var(--ok-bg)] via-card to-card';

export const healthOkGlowClass =
  'bg-[color:color-mix(in_oklab,var(--ok)_18%,transparent)]';

export const healthOkIconTileClass = 'bg-[color:var(--ok)] text-white';

/** Soft ok icon chip for dense health rows (not hero marks). */
export const healthOkIconSoftClass =
  'bg-[color:var(--ok-bg)] text-[color:var(--ok-fg)]';

export const healthOkRowClass = 'hover:bg-[color:var(--ok-bg)]';

export const healthOkFgClass = 'text-[color:var(--ok-fg)]';

export const healthOkBarClass = 'bg-[color:var(--ok)]';

/**
 * Attention / setup gap wash. Prefer `--warn-*` (Badge `warning`) over
 * page-local amber stacks when adding new surfaces.
 */
export const healthAttentionSurfaceClass =
  'border-amber-500/30 bg-gradient-to-br from-amber-500/[0.1] via-card to-card';

export const healthAttentionGlowClass = 'bg-amber-500/20';

export const healthAttentionIconTileClass =
  'bg-amber-500/15 text-amber-800 dark:text-amber-300';

/** Attention foreground for icons / trend copy (matches Badge `warning`). */
export const healthAttentionFgClass = 'text-amber-800 dark:text-amber-300';

export const healthAttentionRowClass =
  'bg-amber-500/[0.06] hover:bg-amber-500/[0.1]';

export const healthAttentionBarClass = 'bg-amber-500';

/** Informational callout — `--info-*` tokens (data-quality / setup hints). */
export const healthInfoSurfaceClass =
  'border-[color:var(--info-border)] bg-[color:var(--info-bg)]';

export const healthInfoFgClass = 'text-[color:var(--info-fg)]';
